import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { toCurrency } from "@helpers";
import { GainLossPercents } from "@components";
import { MandatorTypography } from "@components/MandatorTypography";
import { ToggleGroup } from "../ToggleGroup";

const Container = styled.div<{ collapseHeader: boolean }>`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const CurrentValueWrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: ${({ fullWidth }) =>
    fullWidth ? "space-between" : "flex-start"};
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
`;

const MainValueText = styled(MandatorTypography, {
  shouldForwardProp: (prop) => prop !== "collapseHeader",
})<{ collapseHeader: boolean }>`
  margin-right: ${({ theme }) => theme.spacing(4)};
  font-size: 38px;
  line-height: 48px;
  letter-spacing: -0.02em;

  ${({ collapseHeader }) =>
    collapseHeader &&
    `
      font-size: 28px;
      line-height: 36px;
      font-weight: 700;
      letter-spacing: -0.02em;
  `}
`;

interface DashboardHeaderProps {
  totalValue: number;
  changeToday: number;
  currency: string;
  collapseHeader: boolean;
  view?: string;
  handleViewChange?: (
    event: React.MouseEvent<HTMLElement>,
    value: string
  ) => void;
  fullWidth?: boolean;
}

export const DashboardHeader = ({
  totalValue,
  currency = "EUR",
  changeToday,
  collapseHeader,
  view,
  handleViewChange,
  fullWidth,
}: DashboardHeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));

  const showToggleGroup = view && handleViewChange;

  return (
    <Container collapseHeader={collapseHeader}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Typography
          variant={isLessSm ? "body2" : "body1"}
          fontWeight="600"
          color="textPrimary"
        >
          {t("dashboardPage.dashboard.header.subheader")}
        </Typography>
        {showToggleGroup && (
          <ToggleGroup
            value={view}
            handleViewChange={handleViewChange}
            collapseHeader={collapseHeader}
            fullWidth={fullWidth}
          />
        )}
      </Box>
      <CurrentValueWrapper fullWidth={fullWidth}>
        <MainValueText variant="h2" collapseHeader={collapseHeader}>
          {toCurrency(totalValue, currency)}
        </MainValueText>
        <GainLossPercents value={changeToday} size="medium" />
      </CurrentValueWrapper>
    </Container>
  );
};
